import { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import BackButton from '../components/BackButton';
import ConfirmDialog from '../components/ConfirmDialog';
import { boosterEndsAt, boosterTimeLeft, formatBoosterEnd } from './stage/stageConfig';
import { Zap, Crown, Users, Swords, Check, Briefcase, Film, Music, Megaphone, Star, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BOOSTER_ICONS = {
  captain_boost:      Crown,
  ruler_booster:      Star,
  ceo_booster:        Briefcase,
  actor_booster:      Film,
  athlete_booster:    Swords,
  musician_booster:   Music,
  influencer_booster: Megaphone,
  wildcard:           Zap,
  stage_boost:        Users,
  // Back-compat alias for any old record still on disk
  bench_boost:        Users,
};

const BOOSTER_COLORS = {
  captain_boost:      'text-yellow-500',
  ruler_booster:      'text-purple-700',
  ceo_booster:        'text-blue-500',
  actor_booster:      'text-orange-500',
  athlete_booster:    'text-green-500',
  musician_booster:   'text-amber-500',
  influencer_booster: 'text-pink-500',
  wildcard:           'text-red-500',
  stage_boost:        'text-cyan-500',
  bench_boost:        'text-cyan-500',
};

export default function BoostersPage({ teamId, onBack }) {
  const [available, setAvailable] = useState({});
  const [myBoosters, setMyBoosters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(null);
  const [confirmBooster, setConfirmBooster] = useState(null); // {id, name, description}

  useEffect(() => {
    Promise.all([
      api.get('/boosters'),
      api.get('/boosters/my')
    ]).then(([avRes, myRes]) => {
      setAvailable(avRes.data.boosters || {});
      setMyBoosters(myRes.data.boosters || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // ─── Currently-active Booster detection (v5.7.38) ────────────────────
  // A Booster is "active this week" if it was activated in the last 7 days.
  // We surface this so:
  //   1. The user sees a pinned "Active now" banner.
  //   2. We disable Activate buttons on all OTHER Boosters (backend will
  //      409 them anyway, but we want the UI to make the rule obvious).
  const activeBooster = useMemo(() => {
    const used = myBoosters.filter(b => b.used && b.activated_at);
    if (used.length === 0) return null;
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const recent = used
      .filter(b => new Date(b.activated_at).getTime() >= weekAgo)
      .sort((a, b) => new Date(b.activated_at) - new Date(a.activated_at));
    return recent[0] || null;
  }, [myBoosters]);

  // 2026-09-08 — exact end (server `expires_at`, else activated + 7d) so the
  // page says "until Tue, Sep 15, 4:12 PM · 6d 3h left".
  const activeEndsAt = useMemo(
    () => (activeBooster ? boosterEndsAt(activeBooster.activated_at, activeBooster.expires_at) : null),
    [activeBooster],
  );
  const activeExpiresLabel = useMemo(() => {
    if (!activeEndsAt) return null;
    const left = boosterTimeLeft(activeEndsAt);
    return left === 'expired' ? 'just expired' : `until ${formatBoosterEnd(activeEndsAt)} · ${left} left`;
  }, [activeEndsAt]);

  const handleActivate = async (boosterId) => {
    if (!teamId) { toast.error('No team selected'); return; }
    setActivating(boosterId);
    try {
      const res = await api.post('/boosters/activate', { team_id: teamId, booster_id: boosterId });
      const endsAt = boosterEndsAt(res.data.activated_at, res.data.expires_at);
      toast.success(`${res.data.message || 'Booster is live.'} Active until ${formatBoosterEnd(endsAt)}.`);
      // Update the activated Booster's `used` flag IN PLACE so the card
      // doesn't visibly jump to the bottom of the list — the user
      // explicitly reported this jump as confusing in v5.7.38.
      setMyBoosters(prev => prev.map(b =>
        b.id === boosterId
          ? { ...b, used: true, activated_at: res.data.activated_at || new Date().toISOString(), expires_at: res.data.expires_at }
          : b
      ));
      setConfirmBooster(null);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.message || 'Activation failed');
      toast.error(msg);
    } finally { setActivating(null); }
  };

  const requestActivate = (booster) => {
    if (activeBooster) {
      const activeName = available[activeBooster.booster_type]?.name || 'another Booster';
      toast.error(`${activeName} is still active this week. Only one Booster can run at a time.`);
      return;
    }
    const info = available[booster.booster_type] || {};
    setConfirmBooster({
      id: booster.id,
      name: info.name || booster.booster_type,
      description: info.description || '',
    });
  };

  if (loading) {
    return <div className="p-4 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full rounded-2xl" />)}</div>;
  }

  return (
    <div className="p-4" data-testid="boosters-page">
      {onBack && <BackButton onClick={onBack} />}
      <h2 className="text-lg font-semibold tracking-tight mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
        <Zap className="h-5 w-5 inline mr-2 text-[hsl(var(--stage-ember))]" />
        Boosters
      </h2>
      <p className="text-xs text-muted-foreground mb-4">
        One Booster at a time, one shot each. The 7-day clock starts the
        moment you activate (not on the gameweek boundary), and that Booster
        is spent for the rest of the league. Multipliers stack: captain 2x ×
        a 2x Booster = 4x.
      </p>

      {/* Active chip banner */}
      {activeBooster && (
        <div
          className="flex items-center gap-3 bg-[hsl(var(--accent)/0.1)] border border-[hsl(var(--accent)/0.3)] rounded-2xl px-4 py-3 mb-4"
          data-testid="boosters-active-banner"
        >
          <Sparkles className="h-5 w-5 text-[hsl(var(--accent))] shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">
              {available[activeBooster.booster_type]?.name || activeBooster.booster_type} is live
            </p>
            <p className="text-[11px] text-muted-foreground">
              {available[activeBooster.booster_type]?.description || ''}
              {activeExpiresLabel && <> · <span className="tabular-nums">{activeExpiresLabel}</span></>}
            </p>
          </div>
        </div>
      )}

      {/* Unified list — cards stay in their original position and grey
          out once spent (instead of jumping to a "Used" section). */}
      <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">
        Your Boosters ({myBoosters.length})
      </h3>
      <div className="space-y-2 mb-6">
        {myBoosters.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">
            No boosters available. Earn more through gameplay!
          </p>
        )}
        {myBoosters.map((booster) => {
          const info = available[booster.booster_type] || {};
          const Icon = BOOSTER_ICONS[booster.booster_type] || Zap;
          const color = BOOSTER_COLORS[booster.booster_type] || 'text-muted-foreground';
          const isUsed = !!booster.used;
          const isLiveOne = activeBooster && activeBooster.id === booster.id;
          return (
            <div
              key={booster.id}
              className={`flex items-center gap-3 bg-card border rounded-2xl px-4 py-3 transition-opacity ${
                isUsed ? 'border-border/40 opacity-50' : 'border-border/60'
              } ${isLiveOne ? '!opacity-100 ring-1 ring-[hsl(var(--accent)/0.4)]' : ''}`}
              data-testid={`booster-row-${booster.booster_type}`}
            >
              <div className={`h-10 w-10 rounded-full flex items-center justify-center bg-secondary ${isUsed ? 'text-muted-foreground' : color}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">{info.name || booster.booster_type}</p>
                <p className="text-xs text-muted-foreground">{info.description || ''}</p>
              </div>
              {isUsed ? (
                <Badge variant="secondary" className="text-[10px]">
                  <Check className="h-3 w-3 mr-1" />
                  {isLiveOne ? 'Active' : 'Used'}
                </Badge>
              ) : (
                <Button
                  size="sm"
                  className="h-8"
                  onClick={() => requestActivate(booster)}
                  disabled={activating === booster.id || !!activeBooster}
                  data-testid={`activate-booster-${booster.id}`}
                >
                  <Zap className="h-3 w-3 mr-1" />
                  {activating === booster.id ? '...' : 'Activate'}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Booster Guide */}
      <div className="mt-6 bg-secondary/30 border border-border/40 rounded-xl p-4">
        <h4 className="text-sm font-semibold mb-2">How Boosters Work</h4>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li>• Each booster lasts one week (7 days) after activation</li>
          <li>• Captain Boost: increases captain multiplier from 2× to 3×</li>
          <li>• Category Boosters: double earnings for all people of that type (Ruler, CEO, Actor, Athlete, Artist, Influencer)</li>
          <li>• Wildcard: one free transfer with no penalty</li>
          <li>• Stage Boost: all people on roster score double</li>
          <li>• Only one booster can be active per week — once spent, it's gone for the rest of the league</li>
        </ul>
      </div>

      <ConfirmDialog
        open={!!confirmBooster}
        title={confirmBooster ? `Activate ${confirmBooster.name}?` : 'Activate Booster'}
        description={
          confirmBooster
            ? `${confirmBooster.description}\n\nRuns for 7 days from the moment you activate — it does NOT reset with the game week. Active until ${formatBoosterEnd(boosterEndsAt(new Date().toISOString()))}.\n\nThis Booster will be used up for the rest of the league. You can only run one Booster at a time. Continue?`
            : ''
        }
        confirmLabel="Activate"
        cancelLabel="Cancel"
        onConfirm={() => confirmBooster && handleActivate(confirmBooster.id)}
        onCancel={() => setConfirmBooster(null)}
      />
    </div>
  );
}
